/**
 * @x9/billing — freemium / entitlement constants shared by mobile and Functions.
 *
 * Product source: docs/meta/prd-subscription-billing-asaas-2026-07.md
 * Seat pricing (2026-08): base Equipe R$35 = 3 seats; +R$10 per extra seat.
 * Asaas HTTP adapter is NOT in this package (server secrets only).
 */

'use strict';

const FEATURE_KEYS = {
  PAID_ACCESS: 'x9_paid_access',
};

const PLAN_IDS = {
  STANDARD: 'plan_x9_standard',
};

const PRICE_IDS = {
  STANDARD_MONTHLY_BRL: 'price_x9_standard_monthly_brl_2500',
  STANDARD_ANNUAL_BRL: 'price_x9_standard_annual_brl_24000',
};

/** Free tier: max owned buckets (excludes virtual "unassigned"). */
const FREE_BUCKET_CAP = 3;

/** Free plan: one named teammate seat. Self-dispatch never consumes it. */
const FREE_INCLUDED_SEATS = 1;

/** Free retention: claim bait starts after this many days. */
const FREE_RETENTION_CLAIM_DAYS = 60;

/** Free retention: hard delete after this many days if still unclaimed. */
const FREE_RETENTION_HARD_DELETE_DAYS = 90;

/**
 * Base Equipe: R$35/mês → 3 seats. Extra seats R$10/mês each (singles).
 * Legacy seat packs of 5 @ R$25 are grandfathered via resolveSeatCount.
 */
const BASE_TEAM_SEATS = 3;
const BASE_TEAM_PRICE_CENTAVOS = 3500;
const EXTRA_SEAT_PRICE_CENTAVOS = 1000;
/** @deprecated Was packs-of-5; now = base team seats for UI copy. */
const SEATS_PER_PACK = BASE_TEAM_SEATS;
/** Grandfather: legacy `seatPacks` × this = seatCount. */
const LEGACY_SEATS_PER_PACK = 5;
/** @deprecated Use BASE_TEAM_PRICE_CENTAVOS. */
const SEAT_PACK_PRICE_CENTAVOS = BASE_TEAM_PRICE_CENTAVOS;

const AMOUNTS_CENTAVOS = {
  MONTHLY: BASE_TEAM_PRICE_CENTAVOS,
  /** Base-only yearly (−20% on 12× monthly). */
  ANNUAL: Math.round(BASE_TEAM_PRICE_CENTAVOS * 12 * 0.8),
};

const CURRENCY = 'BRL';

const PORTAL_ORIGIN = 'https://my.x9.app';

/**
 * Inbound-contribution volume (settled 2026-08-10): pooled monthly allowance
 * on submissions from others (QR/web/contributors); own captures never count.
 * Free tier includes a modest allowance; volume sold in stackable packs; paid
 * Equipe (any seatCount > 0) bundles +150 inbound once (not a volume pack).
 * Extra volume packs stay +250 / R$10. Over-quota submissions are HELD
 * (stored, hidden from owner), never bounced at the scanner.
 */
const INBOUND_FREE_MONTHLY = 5;
const INBOUND_PACK_VOLUME = 250;
const INBOUND_PACK_PRICE_CENTAVOS = 1000;
const SEAT_PACK_INCLUDED_INBOUND = 150;

const PORTAL_PATHS = {
  HOME: '/',
  PRICING: '/pricing',
  /** Merged Assinatura surface (plan + payment + history). */
  BILLING: '/billing',
  /** Legacy alias → /billing (emails / bookmarks). */
  PLAN: '/plan',
  USAGE: '/usage',
  SEATS: '/seats',
  /** Legacy alias → /billing. */
  PAYMENT: '/payment',
  /** Legacy alias → /billing. */
  HISTORY: '/history',
  ACCOUNT: '/account',
  CHECKOUT: '/checkout',
  PENDING: '/pending',
  SIGNIN: '/signin',
  EMAIL: '/email',
  VERIFY_EMAIL: '/verify-email',
  /** Lifecycle email click tracking (not an SPA route). */
  GO: '/go',
};

/** Sanity caps on self-service quantities (bigger = talk to us). */
const MAX_SEATS = 200;
const MAX_EXTRA_SEATS = MAX_SEATS - BASE_TEAM_SEATS;
/** @deprecated Prefer MAX_SEATS; kept for callers that still think in packs. */
const MAX_SEAT_PACKS = 40;
const MAX_VOLUME_PACKS = 40;

/** Yearly commitment discount on the pack total. */
const YEARLY_DISCOUNT = 0.2;

/**
 * Resolve seat capacity from subscription/entitlement fields.
 * Prefer `seatCount`; bridge legacy `seatPacks * 5` (grandfather capacity).
 * Paid without either field → base team (3).
 *
 * @param {object|null|undefined} row
 * @param {{ defaultPaid?: number, defaultFree?: number }} [opts]
 * @returns {number}
 */
function resolveSeatCount(row, opts = {}) {
  const defaultPaid = opts.defaultPaid !== undefined ? opts.defaultPaid : BASE_TEAM_SEATS;
  if (Number.isFinite(Number(row?.seatCount))) {
    return Math.max(0, Math.floor(Number(row.seatCount)));
  }
  if (Number.isFinite(Number(row?.seatPacks))) {
    return Math.max(0, Math.floor(Number(row.seatPacks)) * LEGACY_SEATS_PER_PACK);
  }
  if (Number.isFinite(Number(row?.extraSeats))) {
    return BASE_TEAM_SEATS + Math.max(0, Math.floor(Number(row.extraSeats)));
  }
  return Math.max(0, defaultPaid);
}

/**
 * Price a seatCount + volume combination.
 * Paid Equipe: base R$35 (3 seats) + R$10 × extra seats + volume packs.
 * seatCount=0 + volumePacks>=1 = volume-only (QR-heavy persona).
 *
 * Accepts legacy `seatPacks` → seatCount = packs × 5 for grandfather pricing.
 *
 * @param {{
 *   seatCount?: number,
 *   extraSeats?: number,
 *   seatPacks?: number,
 *   volumePacks?: number,
 *   interval?: 'MONTHLY'|'YEARLY',
 * }} [input]
 */
function packPricing({
  seatCount,
  extraSeats,
  seatPacks,
  volumePacks = 0,
  interval = 'MONTHLY',
} = {}) {
  let sc;
  if (seatCount !== undefined && seatCount !== null && seatCount !== '') {
    sc = Math.floor(Number(seatCount));
  } else if (extraSeats !== undefined && extraSeats !== null && extraSeats !== '') {
    const ex = Math.floor(Number(extraSeats));
    if (!Number.isFinite(ex) || ex < 0 || ex > MAX_EXTRA_SEATS) {
      throw new Error('invalid_extra_seats');
    }
    sc = BASE_TEAM_SEATS + ex;
  } else if (seatPacks !== undefined && seatPacks !== null && seatPacks !== '') {
    const sp = Math.floor(Number(seatPacks));
    if (!Number.isFinite(sp) || sp < 0 || sp > MAX_SEAT_PACKS) {
      throw new Error('invalid_seat_packs');
    }
    sc = sp * LEGACY_SEATS_PER_PACK;
  } else {
    sc = BASE_TEAM_SEATS;
  }

  if (!Number.isFinite(sc) || sc < 0 || sc > MAX_SEATS) {
    throw new Error('invalid_seat_count');
  }
  if (sc > 0 && sc < BASE_TEAM_SEATS) {
    throw new Error('invalid_seat_count');
  }

  const vp = Math.floor(Number(volumePacks));
  if (!Number.isFinite(vp) || vp < 0 || vp > MAX_VOLUME_PACKS) {
    throw new Error('invalid_volume_packs');
  }
  if (sc + vp < 1) throw new Error('empty_subscription');

  const iv = String(interval).toUpperCase() === 'YEARLY' ? 'YEARLY' : 'MONTHLY';
  const extra = sc > 0 ? sc - BASE_TEAM_SEATS : 0;
  const monthlyCents = (sc > 0
    ? BASE_TEAM_PRICE_CENTAVOS + extra * EXTRA_SEAT_PRICE_CENTAVOS
    : 0) + vp * INBOUND_PACK_PRICE_CENTAVOS;
  const chargeCents = iv === 'YEARLY'
    ? Math.round(monthlyCents * 12 * (1 - YEARLY_DISCOUNT))
    : monthlyCents;

  return {
    seatCount: sc,
    extraSeats: extra,
    volumePacks: vp,
    monthlyCents,
    chargeCents,
    interval: iv,
    /** @deprecated Compat: ceil(seatCount / 5) for old UIs; prefer seatCount. */
    seatPacks: sc > 0 ? Math.ceil(sc / LEGACY_SEATS_PER_PACK) : 0,
  };
}

/**
 * Whole calendar months from `fromYmd` until `renewalYmd` is reached
 * (ceil-style: any remaining days count as at least 1 month).
 * @param {string|null|undefined} renewalYmd YYYY-MM-DD
 * @param {string|null|undefined} [fromYmd] YYYY-MM-DD (defaults to today UTC date)
 * @returns {number}
 */
function monthsUntilYmd(renewalYmd, fromYmd) {
  const renew = String(renewalYmd || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(renew)) return 0;
  let from = String(fromYmd || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(from)) {
    from = new Date().toISOString().slice(0, 10);
  }
  if (renew <= from) return 0;
  const [fy, fm] = from.split('-').map(Number);
  const [ry, rm] = renew.split('-').map(Number);
  let months = (ry - fy) * 12 + (rm - fm);
  const fromDay = Number(from.slice(8, 10));
  const renewDay = Number(renew.slice(8, 10));
  // If renewal day-of-month is still ahead within the month span, keep months;
  // if we've already passed that day in the final month gap, months already counts it.
  // Any positive remainder of days → at least 1.
  if (months <= 0) return 1;
  // When renew day < from day, the last partial month is already included in `months`
  // via the month index diff (e.g. Aug 20 → Sep 1 = 1 month).
  if (renewDay < fromDay && months > 0) {
    /* keep months as-is */
  }
  return Math.min(24, Math.max(1, months));
}

/**
 * Mid-cycle charge for a YEARLY plan capacity increase:
 * delta monthly × open months × yearly discount (−20%).
 * Decreases / no-ops → 0.
 *
 * @param {{
 *   currentMonthlyCents: number,
 *   targetMonthlyCents: number,
 *   openMonths: number,
 * }} input
 * @returns {{ deltaMonthlyCents: number, openMonths: number, chargeNowCents: number }}
 */
function yearlyMidCycleUpgradeCharge({
  currentMonthlyCents = 0,
  targetMonthlyCents = 0,
  openMonths = 0,
} = {}) {
  const cur = Math.max(0, Math.floor(Number(currentMonthlyCents) || 0));
  const tgt = Math.max(0, Math.floor(Number(targetMonthlyCents) || 0));
  const months = Math.max(0, Math.floor(Number(openMonths) || 0));
  const deltaMonthlyCents = Math.max(0, tgt - cur);
  if (deltaMonthlyCents <= 0 || months <= 0) {
    return { deltaMonthlyCents: 0, openMonths: months, chargeNowCents: 0 };
  }
  const chargeNowCents = Math.round(deltaMonthlyCents * months * (1 - YEARLY_DISCOUNT));
  return { deltaMonthlyCents, openMonths: months, chargeNowCents };
}

/**
 * Plan a seat/volume change on an active subscription:
 *  - INCREASES apply immediately (capacity now);
 *  - MONTHLY: billed from next invoice; YEARLY increases: mid-cycle stub charge
 *    (see yearlyMidCycleUpgradeCharge) + next renewal at new yearly value;
 *  - DECREASES are pended to the next renewal;
 *  - the provider subscription value is always set to the TARGET quantity.
 *
 * @param {{ seatCount?: number, seatPacks?: number, volumePacks?: number }} current
 * @param {{ seatCount?: number, extraSeats?: number, seatPacks?: number, volumePacks: number }} target
 * @param {'MONTHLY'|'YEARLY'} [interval]
 */
function planPackChange(current, target, interval = 'MONTHLY') {
  const curSeatCount = resolveSeatCount(current, { defaultPaid: BASE_TEAM_SEATS });
  const cur = {
    seatCount: curSeatCount,
    volumePacks: Math.max(0, Math.floor(Number(current?.volumePacks ?? 0)) || 0),
  };
  const tgt = packPricing({
    seatCount: target?.seatCount,
    extraSeats: target?.extraSeats,
    seatPacks: target?.seatPacks,
    volumePacks: target?.volumePacks,
    interval,
  });
  const immediate = {
    seatCount: Math.max(cur.seatCount, tgt.seatCount),
    volumePacks: Math.max(cur.volumePacks, tgt.volumePacks),
  };
  const pending = {
    seatCount: tgt.seatCount < cur.seatCount ? tgt.seatCount : null,
    volumePacks: tgt.volumePacks < cur.volumePacks ? tgt.volumePacks : null,
  };
  const changed = tgt.seatCount !== cur.seatCount || tgt.volumePacks !== cur.volumePacks;
  return {
    immediate,
    pending,
    target: { seatCount: tgt.seatCount, volumePacks: tgt.volumePacks },
    monthlyCents: tgt.monthlyCents,
    chargeCents: tgt.chargeCents,
    changed,
    /** @deprecated Prefer seatCount fields. */
    immediateLegacy: {
      seatPacks: immediate.seatCount > 0
        ? Math.ceil(immediate.seatCount / LEGACY_SEATS_PER_PACK)
        : 0,
      volumePacks: immediate.volumePacks,
    },
  };
}

/**
 * @param {object|null|undefined} entitlement
 * @param {Date|number} [now]
 * @returns {boolean}
 */
function isEntitlementActive(entitlement, now = new Date()) {
  if (!entitlement || entitlement.status !== 'ACTIVE') return false;
  if (entitlement.featureKey && entitlement.featureKey !== FEATURE_KEYS.PAID_ACCESS) {
    return false;
  }
  const t = now instanceof Date ? now.getTime() : Number(now);
  if (entitlement.validFrom) {
    const from = new Date(entitlement.validFrom).getTime();
    if (!Number.isNaN(from) && t < from) return false;
  }
  if (entitlement.validUntil) {
    const until = new Date(entitlement.validUntil).getTime();
    if (!Number.isNaN(until) && t >= until) return false;
  }
  return true;
}

/**
 * Count buckets that consume the free-tier cap.
 * @param {Array<{ id?: string }>} buckets
 * @returns {number}
 */
function countBillableBuckets(buckets) {
  if (!Array.isArray(buckets)) return 0;
  return buckets.filter((b) => b && b.id && b.id !== 'unassigned').length;
}

/**
 * @param {number} currentBillableCount
 * @param {boolean} hasPaidAccess
 * @returns {boolean} true if creating one more bucket is allowed
 */
function canCreateBucket(currentBillableCount, hasPaidAccess) {
  if (hasPaidAccess) return true;
  return currentBillableCount < FREE_BUCKET_CAP;
}

/**
 * Deep-link / web URL for billing portal (checkout or manage).
 * @param {string} [path]
 * @param {Record<string, string>} [query]
 * @returns {string}
 */
function portalUrl(path = '/', query) {
  const p = path.startsWith('/') ? path : `/${path}`;
  const url = new URL(`${PORTAL_ORIGIN}${p}`);
  if (query && typeof query === 'object') {
    for (const [k, v] of Object.entries(query)) {
      if (v != null && v !== '') url.searchParams.set(k, String(v));
    }
  }
  return url.toString();
}

module.exports = {
  FEATURE_KEYS,
  PLAN_IDS,
  PRICE_IDS,
  BASE_TEAM_SEATS,
  BASE_TEAM_PRICE_CENTAVOS,
  EXTRA_SEAT_PRICE_CENTAVOS,
  LEGACY_SEATS_PER_PACK,
  SEATS_PER_PACK,
  SEAT_PACK_PRICE_CENTAVOS,
  INBOUND_FREE_MONTHLY,
  INBOUND_PACK_VOLUME,
  INBOUND_PACK_PRICE_CENTAVOS,
  SEAT_PACK_INCLUDED_INBOUND,
  FREE_BUCKET_CAP,
  FREE_INCLUDED_SEATS,
  FREE_RETENTION_CLAIM_DAYS,
  FREE_RETENTION_HARD_DELETE_DAYS,
  AMOUNTS_CENTAVOS,
  CURRENCY,
  PORTAL_ORIGIN,
  PORTAL_PATHS,
  MAX_SEATS,
  MAX_EXTRA_SEATS,
  MAX_SEAT_PACKS,
  MAX_VOLUME_PACKS,
  YEARLY_DISCOUNT,
  resolveSeatCount,
  packPricing,
  planPackChange,
  monthsUntilYmd,
  yearlyMidCycleUpgradeCharge,
  isEntitlementActive,
  countBillableBuckets,
  canCreateBucket,
  portalUrl,
};
